import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, throwError } from 'rxjs';
import { map, catchError, switchMap, finalize, tap, take, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'environments/environment';
import { AppConsts } from '@shared/AppConsts';
import { AppPreBootstrap } from 'AppPreBootstrap';
@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = [];

  // public fields
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private refreshTimer: any;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  private refreshInProgress$: Observable<any | null> | null = null;
  private introspectInProgress$: Observable<any | null> | null = null;

  refreshToken(): Observable<any | null> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    this.refreshInProgress$ = this.refresh().pipe(
      tap((auth: any) => {
        this.handleLogin(auth);
      }),
      finalize(() => {
        this.refreshInProgress$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInProgress$;
  }

  introspectToken(token: string): Observable<any | null> {
    if (this.introspectInProgress$) {
      return this.introspectInProgress$;
    }

    this.introspectInProgress$ = this.introspect(token).pipe(
      tap((isActive: any) => {
        if (isActive) {
          let expires_in = this.getExpiresInCookie();
          if (this.refreshTimer) clearTimeout(this.refreshTimer);
          const refreshDelay = new Date(expires_in).getTime() - Date.now() - (30 * 1000);
          this.refreshTimer = setTimeout(() => this.refreshToken().subscribe(), refreshDelay);
        }
      }),
      finalize(() => {
        this.introspectInProgress$ = null;
      }),
      shareReplay(1)
    );

    return this.introspectInProgress$;
  }

  private handleLogin(auth: any | null) {
    if (!auth) return;
    
    // Sử dụng ABP token management
    AppPreBootstrap.setNewTokenLocalStorage(auth.accessToken);

    // Lưu expires_in vào cookie thay vì localStorage
    const expiresDate = new Date(Date.now() + (Number(auth.expiresIn) || 0) * 1000);
    this.setExpiresInCookie(expiresDate.toString());
    
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const refreshDelay = ((Number(auth.expiresIn) || 0) - 30) * 1000;
    this.refreshTimer = setTimeout(() => this.refreshToken().subscribe(), refreshDelay);
  }

  public reloadConfig(): Observable<any> {
    const cookieLangValue = "vi";
    const token = abp.auth.getToken();

    let requestHeaders: { [key: string]: string } = {
      '.AspNetCore.Culture': ('c=' + cookieLangValue + '|uic=' + cookieLangValue),
      'Abp.TenantId': '1'
    };

    if (token) {
      requestHeaders['Authorization'] = 'Bearer ' + token;
    }

    return this.http.get(`${AppConsts.remoteServiceBaseUrl}/AbpUserConfiguration/GetAll`, {
      headers: requestHeaders
    }).pipe(
      tap(configData => {
        $.extend(true, abp, configData);
        abp.clock.provider = this.getCurrentClockProvider(configData.clock.provider);
      })
    );
  }

  private getCurrentClockProvider(currentProviderName: string): any {
    if (currentProviderName === 'unspecifiedClockProvider') {
      return abp.timing.unspecifiedClockProvider;
    }
    if (currentProviderName === 'utcClockProvider') {
      return abp.timing.utcClockProvider;
    }
    return abp.timing.localClockProvider;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
  }

  refresh(): Observable<any> {
    return this.http.post<any>(`${environment.authAPIUrl}v1/account/refreshtoken`, {}, { withCredentials: true });
  }

  introspect(token: string): Observable<any> {
    return this.http.post<any>(
      `${environment.authAPIUrl}v1/account/instropect`,
      { token: token },
      { withCredentials: true }
    );
  }
  authenticateSaleApp(token: string): Observable<any> {
     const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.post<any>(
      `${AppConsts.remoteServiceBaseUrl}/api/TokenAuth/AuthenticateFromUserService`,
      { },
      { headers: headers, withCredentials: true }
    );
  }

  logout(accessToken: string): Observable<any> {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    return this.http.post<string>(
      `${environment.authAPIUrl}v1/account/logout`,
      {
        accessToken: accessToken
      },
      { withCredentials: true }
    );
  }

  hasValidToken(): boolean {
    const token = AppPreBootstrap.getTokenLocalstorage(
      AppConsts.authorization.newAccessToken
    );
    let expiresIn = this.getExpiresInCookie();
    if (expiresIn) {
      const dateNow = new Date();
      const expiresDate = new Date(expiresIn);
      if (dateNow >= expiresDate) {
        return false;
      }
    }
    return !!token;
  }

  getCurrentToken(): string | null {
    return AppPreBootstrap.getTokenLocalstorage(AppConsts.authorization.newAccessToken);
  }

  clearToken(): void {
    abp.auth.clearToken();
    this.clearExpiresInCookie();
    AppPreBootstrap.clearTokenCookies();
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private setExpiresInCookie(expiresIn: string): void {
    const cookieName = 'token_expires_in';
    const cookieValue = expiresIn;
    const expiryDate = new Date(expiresIn);
    
    abp.utils.setCookieValue(cookieName, cookieValue, expiryDate, '/');
  }

  private getExpiresInCookie(): string | null {
    const cookieName = 'token_expires_in';
    return abp.utils.getCookieValue(cookieName);
  }

  private clearExpiresInCookie(): void {
    const cookieName = 'token_expires_in';
    abp.utils.deleteCookie(cookieName, '/');
  }
}
